import { useState } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { User } from "@shared/schema";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { getInitials } from "@/lib/utils";
import { 
  User as UserIcon, 
  FileText, 
  FileBarChart, 
  LogOut, 
  ChevronDown,
  KeyRound
} from "lucide-react";

interface UserMenuProps {
  user: User;
}

export function UserMenu({ user }: UserMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { logoutMutation } = useAuth();
  const [, navigate] = useLocation();
  
  const handleLogout = () => {
    logoutMutation.mutate();
    setOpen(false);
    navigate("/auth");
  };
  
  const navigateTo = (path: string) => {
    navigate(path);
    setOpen(false);
  };
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center gap-2 text-white">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials(user.username || user.email || "User")}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:block text-left">
            <p className="text-sm font-medium">{user.role}: {user.username || user.email || "User"}</p>
          </div>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          onClick={() => navigateTo("/profile")}
          className="cursor-pointer"
        >
          <UserIcon className="mr-2 h-4 w-4" />
          <span>{t("userMenu.profile")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigateTo("/analyses")}
          className="cursor-pointer"
        >
          <FileText className="mr-2 h-4 w-4" />
          <span>{t("userMenu.savedAnalyses")}</span>
        </DropdownMenuItem>
        
        {user.role === "Admin" && (
          <>
            <DropdownMenuItem
              onClick={() => navigateTo("/settings/system")}
              className="cursor-pointer"
            >
              <KeyRound className="mr-2 h-4 w-4" />
              <span>System Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => navigateTo("/audit-logs")}
              className="cursor-pointer"
            >
              <FileBarChart className="mr-2 h-4 w-4" />
              <span>{t("userMenu.auditLogs")}</span>
            </DropdownMenuItem>
          </>
        )}
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer"
          disabled={logoutMutation.isPending}
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t("userMenu.logout")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
